import MiniSearch from "minisearch";
import type { MarketplaceEntry, ExtensionType } from "../types/index";
import type { GitHubSource } from "@contracts/contract-1-sqs-envelope";
import type { SupportedClient } from "@contracts/contract-2-plugin-store";
import { isOfficial } from "./official";

export interface SearchOptions {
  query: string;
  supportedClients?: SupportedClient[];
  categories?: string[];
  extensionType?: ExtensionType;
  /** Narrow to entries on the curated Official CAI allowlist. */
  officialOnly?: boolean;
  sortBy?: "name" | "updated" | "created" | "official";
  sortOrder?: "asc" | "desc";
}

const INDEXED_FIELDS: (keyof MarketplaceEntry)[] = [
  "pluginName", "displayName", "description",
  "orgOrOwner", "repoName", "author",
  "installName", "skillPath",
];

function pluginId(p: MarketplaceEntry): string {
  if (p.installName) return p.installName;
  const base = `${p.source}--${p.orgOrOwner}--${p.repoName}`;
  return p.skillPath ? `${base}--${p.skillPath}` : base;
}

interface SearchIndex {
  mini: MiniSearch<MarketplaceEntry>;
  ids: Map<MarketplaceEntry, string>;
}

function buildIndex(plugins: MarketplaceEntry[]): SearchIndex {
  const ids = new Map<MarketplaceEntry, string>();
  const docs = plugins.map((p) => {
    const id = pluginId(p);
    ids.set(p, id);
    return { ...p, _searchId: id };
  });

  const mini = new MiniSearch<MarketplaceEntry>({
    idField: "_searchId",
    fields: [...INDEXED_FIELDS, "_keywordsText"],
    extractField: (doc, field) => {
      if (field === "_searchId") return (doc as any)._searchId;
      if (field === "_keywordsText") {
        const kw = (doc as MarketplaceEntry).keywords;
        return kw ? kw.join(" ") : "";
      }
      return (doc as unknown as Record<string, string>)[field] ?? "";
    },
    searchOptions: {
      prefix: (_term, i, terms) => i === terms.length - 1,
      fuzzy: 0.2,
      combineWith: "AND",
    },
  });

  mini.addAll(docs);
  return { mini, ids };
}

const indexCache = new WeakMap<MarketplaceEntry[], SearchIndex>();

function getIndex(plugins: MarketplaceEntry[]): SearchIndex {
  let cached = indexCache.get(plugins);
  if (!cached) {
    cached = buildIndex(plugins);
    indexCache.set(plugins, cached);
  }
  return cached;
}

export function searchPlugins(plugins: MarketplaceEntry[], query: string): MarketplaceEntry[] {
  if (!query.trim()) return plugins;

  const { mini, ids } = getIndex(plugins);
  const hits = mini.search(query);
  const matchedIds = new Set(hits.map((h) => h.id));
  return plugins.filter((p) => matchedIds.has(ids.get(p)));
}

/**
 * Filter plugins by source
 */
export function filterBySource(plugins: MarketplaceEntry[], source: GitHubSource): MarketplaceEntry[] {
  return plugins.filter((p) => p.source === source);
}

/**
 * Filter plugins by supported client
 */
export function filterBySupportedClient(
  plugins: MarketplaceEntry[],
  client: SupportedClient,
): MarketplaceEntry[] {
  return plugins.filter((p) => {
    const clients = p.supportedClients && p.supportedClients.length > 0
      ? p.supportedClients
      : ["claude-code"];
    return clients.includes(client);
  });
}

/**
 * Filter plugins by multiple supported clients (OR — must support at least one)
 */
export function filterBySupportedClients(
  plugins: MarketplaceEntry[],
  clients: SupportedClient[],
): MarketplaceEntry[] {
  return plugins.filter((p) => {
    const pluginClients = p.supportedClients && p.supportedClients.length > 0
      ? p.supportedClients
      : ["claude-code"];
    return clients.some((c) => pluginClients.includes(c));
  });
}

/**
 * Filter plugins by skill type
 */
export function filterBySkillType(
  plugins: MarketplaceEntry[],
  skillType: "claude-code" | "claude-cowork" | "copilot"
): MarketplaceEntry[] {
  return plugins.filter((p) => p.skillType === skillType);
}

/**
 * Filter plugins by extension type (plugin vs skill).
 *
 * Strict equality on purpose: an entry whose extensionType is absent/unknown
 * (e.g. a record written before schema 1.3, which predates the field) matches
 * neither "plugin" nor "skill", so it is excluded from those facets but never
 * surfaces under one it doesn't belong to. Such entries stay visible only when
 * no type filter is set.
 */
export function filterByExtensionType(
  plugins: MarketplaceEntry[],
  extensionType: ExtensionType,
): MarketplaceEntry[] {
  return plugins.filter((p) => p.extensionType === extensionType);
}

/**
 * Filter plugins by role category
 */
export function filterByCategory(
  plugins: MarketplaceEntry[],
  category: string,
): MarketplaceEntry[] {
  return plugins.filter((p) =>
    p.categories && p.categories.some(
      (c) => c.toLowerCase() === category.toLowerCase()
    )
  );
}

/**
 * Filter plugins by multiple categories (OR — must match at least one)
 */
export function filterByCategories(
  plugins: MarketplaceEntry[],
  categories: string[],
): MarketplaceEntry[] {
  return plugins.filter((p) =>
    p.categories && p.categories.some(
      (c) => categories.some((cat) => cat.toLowerCase() === c.toLowerCase())
    )
  );
}

/**
 * Filter plugins by keywords (OR — must have at least one)
 */
export function filterByKeywords(
  plugins: MarketplaceEntry[],
  keywords: string[],
): MarketplaceEntry[] {
  return plugins.filter((p) => {
    if (!p.keywords) return false;
    const lower = p.keywords.map((k) => k.toLowerCase());
    return keywords.some((kw) => lower.includes(kw.toLowerCase()));
  });
}

/**
 * Extract unique, sorted categories from a plugin list
 */
export function extractCategories(plugins: MarketplaceEntry[]): string[] {
  const seen = new Map<string, string>();
  for (const plugin of plugins) {
    if (plugin.categories) {
      for (const cat of plugin.categories) {
        const trimmed = cat.trim();
        if (trimmed && !seen.has(trimmed.toLowerCase())) {
          seen.set(trimmed.toLowerCase(), trimmed);
        }
      }
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );
}

/**
 * Sort plugins
 */
export function sortPlugins(
  plugins: MarketplaceEntry[],
  sortBy: "name" | "updated" | "created" | "official" = "name",
  sortOrder: "asc" | "desc" = "asc"
): MarketplaceEntry[] {
  const sorted = [...plugins];
  const multiplier = sortOrder === "asc" ? 1 : -1;

  sorted.sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortBy) {
      case "official": {
        // Official entries first; stable tiebreak by name (ascending). sortOrder
        // is irrelevant here — "Official CAI first" is a fixed ordering.
        const aOfficial = isOfficial(a) ? 1 : 0;
        const bOfficial = isOfficial(b) ? 1 : 0;
        if (aOfficial !== bOfficial) return bOfficial - aOfficial;
        return a.pluginName.toLowerCase().localeCompare(b.pluginName.toLowerCase());
      }

      case "name":
        aValue = a.pluginName.toLowerCase();
        bValue = b.pluginName.toLowerCase();
        return aValue < bValue ? -1 * multiplier : aValue > bValue ? 1 * multiplier : 0;

      case "updated":
        aValue = new Date(a.lastUpdatedAt).getTime();
        bValue = new Date(b.lastUpdatedAt).getTime();
        return (aValue - bValue) * multiplier;

      case "created":
        aValue = new Date(a.firstDiscoveredAt).getTime();
        bValue = new Date(b.firstDiscoveredAt).getTime();
        return (aValue - bValue) * multiplier;

      default:
        return 0;
    }
  });

  return sorted;
}

/**
 * Combined search and filter
 */
export function filterPlugins(plugins: MarketplaceEntry[], options: SearchOptions): MarketplaceEntry[] {
  let results = plugins;

  // Search by query
  if (options.query) {
    results = searchPlugins(results, options.query);
  }

  // Filter by supported clients
  if (options.supportedClients && options.supportedClients.length > 0) {
    results = filterBySupportedClients(results, options.supportedClients);
  }

  // Filter by categories
  if (options.categories && options.categories.length > 0) {
    results = filterByCategories(results, options.categories);
  }

  // Filter by extension type (plugin vs skill)
  if (options.extensionType) {
    results = filterByExtensionType(results, options.extensionType);
  }

  // Narrow to Official CAI allowlist
  if (options.officialOnly) {
    results = results.filter(isOfficial);
  }

  // Sort
  if (options.sortBy) {
    results = sortPlugins(results, options.sortBy, options.sortOrder);
  }

  return results;
}

/**
 * Debounce utility for search input
 */
export type DebouncedFn<T extends (...args: any[]) => void> = ((...args: Parameters<T>) => void) & {
  cancel: () => void;
};

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delayMs: number = 300
): DebouncedFn<T> {
  let timeoutId: ReturnType<typeof setTimeout>;

  const debounced = function debounced(...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delayMs);
  } as DebouncedFn<T>;

  debounced.cancel = () => {
    clearTimeout(timeoutId);
  };

  return debounced;
}
